// US states for the pre-search card's location row and the CSV import.
//
// The card resolves states here, instantly; anything else (a city, "Austin, TX", "bay area")
// goes to GET /api/geo/place, because the metro list lives on the server
// (backend/services/placeResolver.js + metroAreas.js).

export const US_STATES = {
  ALABAMA:'AL',ALASKA:'AK',ARIZONA:'AZ',ARKANSAS:'AR',CALIFORNIA:'CA',
  COLORADO:'CO',CONNECTICUT:'CT',DELAWARE:'DE',FLORIDA:'FL',GEORGIA:'GA',
  HAWAII:'HI',IDAHO:'ID',ILLINOIS:'IL',INDIANA:'IN',IOWA:'IA',
  KANSAS:'KS',KENTUCKY:'KY',LOUISIANA:'LA',MAINE:'ME',MARYLAND:'MD',
  MASSACHUSETTS:'MA',MICHIGAN:'MI',MINNESOTA:'MN',MISSISSIPPI:'MS',MISSOURI:'MO',
  MONTANA:'MT',NEBRASKA:'NE',NEVADA:'NV','NEW HAMPSHIRE':'NH','NEW JERSEY':'NJ',
  'NEW MEXICO':'NM','NEW YORK':'NY','NORTH CAROLINA':'NC','NORTH DAKOTA':'ND',
  OHIO:'OH',OKLAHOMA:'OK',OREGON:'OR',PENNSYLVANIA:'PA','RHODE ISLAND':'RI',
  'SOUTH CAROLINA':'SC','SOUTH DAKOTA':'SD',TENNESSEE:'TN',TEXAS:'TX',
  UTAH:'UT',VERMONT:'VT',VIRGINIA:'VA',WASHINGTON:'WA','WEST VIRGINIA':'WV',
  WISCONSIN:'WI',WYOMING:'WY',
};
export const VALID_STATE_CODES = new Set(Object.values(US_STATES));

// Chip labels: 'TX' → 'Texas'. DC is a valid search state even though the CSV map above omits it.
export const STATE_NAMES = {
  ...Object.fromEntries(Object.entries(US_STATES).map(([name, code]) => [
    code, name.toLowerCase().replace(/\b\w/g, ch => ch.toUpperCase()),
  ])),
  DC: 'Washington DC',
};

// Extract a 2-letter US state code from a location string like "Boulder, CO" or "New York, NY"
export function extractStateCode(raw) {
  if (!raw) return '';
  const s = String(raw).trim();
  // "City, ST" pattern - grab last 2-letter word after a comma
  const m = s.match(/,\s*([A-Za-z]{2})(?:\s|\/|$)/);
  if (m && VALID_STATE_CODES.has(m[1].toUpperCase())) return m[1].toUpperCase();
  // Full state name anywhere in the string
  const upper = s.toUpperCase();
  for (const [name, code] of Object.entries(US_STATES)) {
    if (upper.includes(name)) return code;
  }
  return '';
}

/** Exactly a state: "tx", "TX", "Texas", "new york" → code; anything else (a city too) → ''. */
export function resolveStateLocal(text) {
  const t = String(text || '').trim().replace(/\./g, '').replace(/\s+/g, ' ').toUpperCase();
  if (!t) return '';
  if (/^[A-Z]{2}$/.test(t)) return t === 'DC' || VALID_STATE_CODES.has(t) ? t : '';
  if (t === 'WASHINGTON DC' || t === 'DISTRICT OF COLUMBIA') return 'DC';
  return US_STATES[t] || '';
}

// Codes that are also everyday words in a typed reply ("OK", "HI", "IN"). A reply naming one of
// these states has to spell it out ("Oklahoma"); the location box still takes the code.
const WORDLIKE_CODES = new Set(['OK', 'HI', 'ME', 'OH', 'IN', 'OR', 'AL', 'LA', 'MA', 'PA', 'DE', 'ID']);

// Longest names first so "West Virginia" is not read as "Virginia".
const NAMES_BY_LENGTH = Object.keys(US_STATES).sort((a, b) => b.length - a.length);

/**
 * States named in a typed reply to the card: "in Texas", "TX and CA", "based in new york".
 * Returns { states } when at least one is named, undefined otherwise. Two-letter codes count only
 * in capitals ("in TX"), so ordinary words like "in" or "or" are never read as Indiana or Oregon.
 * Cities are not read here; the location box takes those.
 */
export function extractPlaceAnswer(text) {
  const raw = String(text || '');
  if (!raw.trim()) return undefined;
  let upper = raw.toUpperCase();
  const found = [];
  for (const name of NAMES_BY_LENGTH) {
    const re = new RegExp(`\\b${name}\\b`);
    if (re.test(upper)) {
      found.push(US_STATES[name]);
      upper = upper.replace(re, ' ');
    }
  }
  for (const m of raw.matchAll(/\b([A-Z]{2})\b/g)) {
    const code = m[1];
    if (WORDLIKE_CODES.has(code)) continue;
    if ((VALID_STATE_CODES.has(code) || code === 'DC') && !found.includes(code)) found.push(code);
  }
  return found.length ? { states: found } : undefined;
}

/**
 * The place a typed reply names, for the lookup the location box uses: the words after "in",
 * "located in", "based in" or "from" ("people located in Austin, Texas" → "Austin, Texas"), or
 * the whole reply when it is short enough to be a place on its own ("Austin, Texas"). '' when the
 * reply is long and names no such clause, or has no letters to look up.
 */
export function placeClauseOf(text) {
  const raw = String(text || '').replace(/\s+/g, ' ').trim();
  if (!raw) return '';
  const m = raw.match(/\b(?:located in|based in|living in|live in|in|from)\s+(.+?)(?:[,;]\s*(?:max|no limit|new pool|use existing|any revenue|under|over)\b.*|\s+(?:and|with|max|new pool)\b.*)?$/i);
  const clause = m ? m[1].trim() : (raw.split(' ').length <= 4 ? raw : '');
  return /[a-z]/i.test(clause) ? clause.replace(/[.!?]+$/, '').trim() : '';
}

/**
 * Whether a typed reply says what the place means: 'person' for "people located in", "who live
 * in", "living in"; 'company' for "companies based in", "headquartered in", "HQ in". undefined
 * when the wording does not say, so the card's pre-selected answer stands.
 */
export function placeScopeOf(text) {
  const t = String(text || '').toLowerCase();
  if (/\b(people|persons?|reps?|contacts?|folks|anyone|someone)\b[^.]{0,30}\b(located|living|live|lives|based|residing|reside)\b|\bwho live\b|\bliving in\b|\bresidents?\b/.test(t)) return 'person';
  if (/\b(compan(?:y|ies)|employers?|firms?|businesses|startups?)\b[^.]{0,30}\b(based|headquartered|located|hq)\b|\bheadquartered\b|\bhq\b/.test(t)) return 'company';
  return undefined;
}

/** { states: ['TX'], city: 'Austin' } → "Austin, TX"; { states: ['TX','CA'] } → "TX, CA"; else ''. */
export function describePlace(place) {
  const states = Array.isArray(place?.states) ? place.states : [];
  const city   = String(place?.city || '').trim();
  if (city) return states.length ? `${city}, ${states.join(', ')}` : city;
  return states.join(', ');
}

/** True when a place was chosen. */
export function hasPlace(place) {
  return !!(place && ((Array.isArray(place.states) && place.states.length) || place.city));
}
